import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FaFileAlt,
  FaDollarSign,
  FaFileInvoice,
  FaUser,
  FaHashtag,
  FaImages,
  FaFile,
  FaCalendar,
  FaCommentAlt,
  FaExclamationCircle,
  FaChevronDown,
  FaChevronUp,
  FaGripVertical,
  FaTrash,
} from 'react-icons/fa';
import {
  TypeItemWrapper,
  TypeItemHeader,
  DragHandle,
  OrderBadge,
  TypeMainInfo,
  TypeName,
  FieldCountBadge,
  CustomDescription,
  TypeActions,
  ActionButton,
  ExpandedContent,
  FieldsTitle,
  FieldsGrid,
  FieldBox,
  FieldLeft,
  FieldMandatoryRow,
  FieldIcon,
  FieldName,
  MandatoryText,
} from './styles';
import CheckBox from 'components/CheckBox';

export default function OccurrenceTypeItem({ item, index, isEditing, onDelete, onUpdateFields, isSaving }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lockedByFieldId] = useState(() => {
    const lockedMap = {};

    (item.occurrenceFieldsConfigs || []).forEach((fieldConfig) => {
      lockedMap[fieldConfig.id] = fieldConfig?.status?.slug === 'REQUIRED'
        || fieldConfig?.status?.slug === 'DEFAULT';
    });

    return lockedMap;
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const FIELD_CONFIG_BY_TYPE = {
    string: { icon: FaFileAlt },
    text: { icon: FaFileAlt },
    'text-area': { icon: FaCommentAlt },
    number: { icon: FaDollarSign },
    decimal: { icon: FaDollarSign },
    datetime: { icon: FaCalendar },
    date: { icon: FaCalendar },
    select: { icon: FaFileInvoice },
    'multi-select': { icon: FaHashtag },
    attachment: { icon: FaImages },
    file: { icon: FaFile },
    user: { icon: FaUser },
    reason: { icon: FaExclamationCircle },
  };

  const STATUS_REQUIRED = 'REQUIRED';
  const STATUS_DEFAULT = 'DEFAULT';
  const STATUS_OPTIONAL = 'OPTIONAL';
  const STATUS_SELECTED = 'SELECTED';

  const STATUS_LABELS = {
    [STATUS_REQUIRED]: 'Obrigatorio',
    [STATUS_DEFAULT]: 'Padrao',
    [STATUS_OPTIONAL]: 'Opcional',
    [STATUS_SELECTED]: 'Selecionado',
  };

  const isRequiredStatus = (fieldConfig) => fieldConfig?.status?.slug === STATUS_REQUIRED;
  const isSelectedStatus = (fieldConfig) => {
    const slug = fieldConfig?.status?.slug;

    return slug === STATUS_REQUIRED || slug === STATUS_DEFAULT || slug === STATUS_SELECTED;
  };
  const isLockedStatus = (fieldConfig) => !!lockedByFieldId[fieldConfig?.id];

  const getStatusTemplate = (fieldConfig, slug) => {
    const fromList = (item.occurrenceFieldsConfigs || [])
      .map((config) => config.status)
      .find((status) => status?.slug === slug);

    if (fromList) {
      return fromList;
    }

    const fallbackId = fieldConfig?.status?.id ?? fieldConfig?.statusId ?? 0;

    return {
      id: fallbackId,
      name: STATUS_LABELS[slug] || slug,
      slug,
    };
  };

  const updateFieldStatus = (fieldConfig, slug) => {
    const nextStatus = getStatusTemplate(fieldConfig, slug);

    return {
      ...fieldConfig,
      status: {
        ...nextStatus,
        name: nextStatus?.name || STATUS_LABELS[slug] || slug,
        slug,
      },
      statusId: nextStatus?.id ?? fieldConfig?.statusId,
    };
  };
  const displayName = item.name || 'Sem nome';
  const fieldsCount = (item.occurrenceFieldsConfigs || []).filter((fieldConfig) => isSelectedStatus(fieldConfig)).length;

  const handleToggleField = (fieldConfig) => {
    if (!isEditing) {
      return;
    }

    if (isLockedStatus(fieldConfig)) {
      return;
    }

    const isSelected = isSelectedStatus(fieldConfig);

    const nextStatus = isSelected ? STATUS_OPTIONAL : STATUS_SELECTED;

    onUpdateFields({
      ...item,
      occurrenceFieldsConfigs: (item.occurrenceFieldsConfigs || []).map((field) => (
        field.id === fieldConfig.id ? updateFieldStatus(field, nextStatus) : field
      )),
    });
  };

  const handleToggleMandatory = (fieldConfig) => {
    if (!isEditing) {
      return;
    }

    if (isLockedStatus(fieldConfig)) {
      return;
    }

    const isMandatory = fieldConfig?.status?.slug === STATUS_REQUIRED;
    const nextStatus = isMandatory ? STATUS_SELECTED : STATUS_REQUIRED;

    onUpdateFields({
      ...item,
      occurrenceFieldsConfigs: (item.occurrenceFieldsConfigs || []).map((field) => (
        field.id === fieldConfig.id ? updateFieldStatus(field, nextStatus) : field
      )),
    });
  };

  return (
    <TypeItemWrapper ref={setNodeRef} style={style} isDragging={isDragging}>
      <TypeItemHeader>
        <DragHandle {...attributes} {...listeners}>
          <FaGripVertical />
        </DragHandle>

        <OrderBadge color={item.color}>{index + 1}</OrderBadge>

        <TypeMainInfo>
          <TypeName>{displayName}</TypeName>
          {fieldsCount > 0 && (
            <FieldCountBadge>{fieldsCount} campos</FieldCountBadge>
          )}
        </TypeMainInfo>

        {item.description && (
          <CustomDescription>{item.description}</CustomDescription>
        )}

        <TypeActions>
          <ActionButton
            onClick={() => setIsExpanded(!isExpanded)}
            active={isExpanded}
            disabled={isSaving}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </ActionButton>
          <ActionButton
            danger
            disabled={!isEditing || isSaving}
            onClick={(e) => {
              if (!isEditing) {
                return;
              }
              e.stopPropagation();
              onDelete(item);
            }}
          >
            <FaTrash />
          </ActionButton>
        </TypeActions>
      </TypeItemHeader>

      {isExpanded && (
        <ExpandedContent>
          <FieldsTitle>Campos de preenchimento</FieldsTitle>
          <FieldsGrid>
            {item.occurrenceFieldsConfigs?.map((field) => {
              const isSelected = isSelectedStatus(field);
              const locked = isLockedStatus(field);
              const required = isRequiredStatus(field);
              const isChecked = locked ? true : isSelected;
              const isMandatory = required;
              const Icon = FIELD_CONFIG_BY_TYPE[field.type]?.icon || FaFileAlt;

              return (
                <FieldBox key={field.id} checked={isChecked}>
                  <FieldLeft>
                    <CheckBox
                      label=""
                      checked={isChecked}
                      disabled={!isEditing || locked || isSaving}
                      style={{ margin: 0 }}
                      onChange={() => handleToggleField(field)}
                    />
                    <FieldIcon checked={isChecked}>
                      <Icon />
                    </FieldIcon>
                    <FieldName checked={isChecked}>{field.name}</FieldName>
                  </FieldLeft>
                  <FieldMandatoryRow>
                    <CheckBox
                      label=""
                      checked={isChecked && isMandatory}
                      disabled={!isEditing || locked || isSaving}
                      style={{ margin: 0 }}
                      onChange={() => handleToggleMandatory(field)}
                    />
                    <MandatoryText>Obrigatório</MandatoryText>
                  </FieldMandatoryRow>
                </FieldBox>
              );
            })}
          </FieldsGrid>
        </ExpandedContent>
      )}
    </TypeItemWrapper>
  );
}
